import { Download } from "lucide-react";
import type { HelpHubBranding, HelpHubDownload } from "@/types/help-hub/help-hub";
import { HubSection } from "./HubSection";

/** Human file size. Returns null rather than "0 KB" when we weren't told one. */
const formatSize = (bytes: number | null): string | null => {
  if (!bytes || bytes <= 0) return null;
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
};

/** Short file-type label from the mime type, e.g. "PDF". Never a guess. */
const formatKind = (mimeType: string | null, fileName: string | null): string | null => {
  if (mimeType?.includes("pdf")) return "PDF";
  if (mimeType?.includes("word")) return "DOC";
  if (mimeType?.includes("sheet") || mimeType?.includes("excel")) return "SHEET";
  if (mimeType?.startsWith("image/")) return "IMAGE";
  const extension = fileName?.split(".").pop();
  return extension && extension.length <= 4 ? extension.toUpperCase() : null;
};

interface HubDownloadsSectionProps {
  downloads: HelpHubDownload[];
  branding: HelpHubBranding;
}

/**
 * Manuals and documents the business has published.
 *
 * These are the same `info_links.documents` the live-agent page already offers
 * in its resources rail — v1 of this page simply never forwarded them, so every
 * business that had uploaded a manual watched it not appear on their own help
 * centre.
 */
export const HubDownloadsSection = ({ downloads, branding }: HubDownloadsSectionProps) => {
  if (downloads.length === 0) return null;

  return (
    <HubSection
      id="downloads"
      title="Manuals & documents"
      subtitle="Download and keep."
      genieIcon="files"
      branding={branding}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {downloads.map((download) => {
          const kind = formatKind(download.mimeType, download.fileName);
          const size = formatSize(download.sizeBytes);
          const meta = [kind, size].filter(Boolean).join(" · ");

          return (
            <a
              key={download.id}
              href={download.fileUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center gap-3 rounded-2xl border border-genie-border bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <span
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
                style={{ backgroundColor: `${branding.primaryColor}14` }}
              >
                <Download
                  className="h-4 w-4"
                  style={{ color: branding.primaryColor }}
                  strokeWidth={2.25}
                />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[14px] font-semibold text-genie-text-primary">
                  {download.label}
                </span>
                {meta && (
                  <span className="block text-[12px] text-genie-text-muted">{meta}</span>
                )}
              </span>
            </a>
          );
        })}
      </div>
    </HubSection>
  );
};

export default HubDownloadsSection;
